import { useCallback } from 'react'
import useSWR, { useSWRConfig } from 'swr'
import useSWRInfinite from 'swr/infinite'

import { useAuthToken } from '@/hooks/useAuthToken'
import { useCoachWorkspaceEnabled } from '@/hooks/useCoachWorkspaceEnabled'
import { useLearnerCoaching } from '@/hooks/useLearnerCoaching'
import { useLearnerCoachingEnabled } from '@/hooks/useLearnerCoachingEnabled'
import { type ApiError, createFetcher, createSubmitter } from '@/services/api'
import { notificationsService } from '@/services/notificationsService'
import type { AppNotification, NotificationPage, UnreadNotificationCount } from '@/types/notifications'

/** One panel's worth. "Load more" appends another page rather than widening this. */
const PAGE_SIZE = 10

/** Matches the dispatcher's tick. Polling faster cannot surface anything sooner. */
const COUNT_POLL_MS = 30_000

/**
 * Whether this reader has any coaching surface, and so a bell worth showing.
 *
 * **Either flag, not just the coach one.** The two gate different audiences and
 * roll out to different people at different times: `coach-workspace` opens the
 * tooling to pilot coaches, `learner-coaching` opens the learner's own page to
 * the learners those coaches write to. The bell appears in both shells, and a
 * learner in the `learner-coaching` rollout receives coaching notifications, so
 * gating on the coach flag alone would leave them with notifications and nowhere
 * to read them.
 *
 * Still no role-specific code: this asks whether any coaching surface is open to
 * the reader, not which kind of reader they are.
 *
 * The hooks rather than the route guards, because the bell is an in-page
 * conditional — a transient false on a hard load hides an icon rather than
 * navigating anybody away.
 */
export function useCoachingNotificationsEnabled(): boolean {
  // Both called unconditionally: short-circuiting one would be a conditional
  // hook call.
  const coachWorkspace = useCoachWorkspaceEnabled()
  const learnerCoaching = useLearnerCoachingEnabled()
  const { isLearner, coachingEnabled } = useLearnerCoaching()
  // A learner's rows link to /coaching, so they follow the learner's own coaching configuration.
  if (isLearner) return coachingEnabled
  return coachWorkspace || learnerCoaching
}

/**
 * The unread badge (ENG-3382).
 *
 * A null SWR key when the reader has no coaching surface, so somebody without it
 * makes no request at all rather than polling for a bell they cannot see.
 */
export function useUnreadNotificationCount() {
  const { getToken, isSignedIn } = useAuthToken()
  const flagEnabled = useCoachingNotificationsEnabled()
  const key = isSignedIn && flagEnabled ? notificationsService.getUnreadCountUrl() : null

  const { data, isLoading, mutate } = useSWR<UnreadNotificationCount>(key, createFetcher(getToken), {
    refreshInterval: COUNT_POLL_MS,
    // A bell is read at a glance on arrival, and the count is one indexed
    // lookup, so re-reading it when somebody comes back to the tab is worth the
    // request. This is also what makes the badge drop after navigating to a
    // conversation, which clears server-side on the read.
    revalidateOnFocus: true,
  })

  return {
    unread: data?.unread ?? 0,
    isLoading,
    refresh: mutate,
  }
}

/**
 * The notification list, paged, plus marking read (ENG-3382).
 *
 * Fetched only when the panel is open (`enabled`), because the list is not what
 * the badge polls: the count is one number on every page, and the list is many
 * rows on demand. Sharing a key would mean polling the list too.
 *
 * Paging is by cursor from the page before it, the same shape the coaching
 * thread uses. The API refuses half a cursor, so there is no way to accidentally
 * page by timestamp alone and drop a row.
 */
export function useNotifications(enabled: boolean) {
  const { getToken, isSignedIn } = useAuthToken()
  const flagEnabled = useCoachingNotificationsEnabled()
  const { mutate: mutateGlobal } = useSWRConfig()
  const active = enabled && isSignedIn && flagEnabled

  const getKey = (pageIndex: number, previousPage: NotificationPage | null) => {
    if (!active) return null
    if (pageIndex === 0) return notificationsService.getNotificationsUrl(PAGE_SIZE)
    // A null cursor means the previous page reached the oldest notification, so
    // there is nothing after it to ask for.
    if (!previousPage?.next_cursor) return null
    return notificationsService.getNotificationsUrl(PAGE_SIZE, previousPage.next_cursor)
  }

  const { data, size, setSize, isLoading, error, mutate } = useSWRInfinite<NotificationPage>(
    getKey,
    createFetcher(getToken)
  )

  const notifications: readonly AppNotification[] = data?.flatMap((page) => page.notifications) ?? []
  const hasMore = data ? data[data.length - 1]?.next_cursor !== null : false

  const markRead = useCallback(
    async (notificationIds: readonly string[]) => {
      if (notificationIds.length === 0) return
      await createSubmitter(getToken)(notificationsService.getMarkReadUrl(), {
        notification_ids: notificationIds,
      })
      // Both, and in this order. The list is what the reader is looking at; the
      // badge is what everything else on the page shows, and it lives under a
      // different key, so it has to be told separately or it keeps the old
      // number until the next poll.
      await mutate()
      await mutateGlobal(notificationsService.getUnreadCountUrl())
    },
    [getToken, mutate, mutateGlobal]
  )

  return {
    notifications,
    hasMore,
    isLoading,
    loadError: error as ApiError | undefined,
    loadMore: () => setSize(size + 1),
    markRead,
    refresh: mutate,
  }
}
